<template>
  <!-- AB#21297 — see AssignOrderAdminModal: opened from the ⋯ menu via `open()`. -->
  <button
    v-if="!hideTrigger"
    class="flex gap-1 items-center justify-center text-sm px-2 py-2 h-max font-medium text-[var(--accent)] border-[1px] border-[var(--accent)] bg-surface rounded-[10px] whitespace-nowrap"
    @click="exportOrderPriceModal = true"
  >
    <DocumentCurrencyDollarIcon class="size-5 text-[var(--accent)]" />
    Export Order Data
  </button>
  <Transition name="fade">
    <DrawerDialog
      v-if="exportOrderPriceModal"
      title="Export Order Prices"
      data-test="export-price-drawer"
      :show-cancel-button="false"
      :show-save-button="false"
      :custom-footer="true"
      :show-footer="true"
      :show-close-button="true"
      @closeEvent="closeEvent"
    >
      <template #modal-content>
        <div class="my-2 flex justify-between">
          <div class="w-full md:w-[45%] flex flex-col gap-2">
            <label for="from-date">From Date</label>
            <input
              type="date"
              id="from-date"
              v-model="fromDate"
              class="px-3 py-1 border-[1px] border-line bg-surface text-ink focus:outline-none rounded-md"
            />
          </div>
          <div class="w-full md:w-[45%] flex flex-col gap-2">
            <label for="end-date">End Date</label>
            <input
              type="date"
              id="end-date"
              v-model="endDate"
              class="px-3 py-1 border-[1px] border-line bg-surface text-ink focus:outline-none rounded-md"
            />
          </div>
        </div>
        <div class="my-2 flex gap-2 flex-wrap">
          <button
            @click="exportType = 'order'"
            :class="[
              'px-3 py-2 text-base rounded-lg h-max transition-colors',
              exportType === 'order'
                ? 'bg-[var(--accent)] text-white'
                : 'bg-surface text-[var(--accent)] border-[1px] border-[var(--accent)]',
            ]"
          >
            Export: Order level
          </button>
          <button
            @click="exportType = 'order_item'"
            :class="[
              'px-3 py-2 text-base rounded-lg h-max transition-colors',
              exportType === 'order_item'
                ? 'bg-[var(--accent)] text-white'
                : 'bg-surface text-[var(--accent)] border-[1px] border-[var(--accent)]',
            ]"
          >
            Export: Item level
          </button>
          <button
            @click="exportType = 'roster'"
            :class="[
              'px-3 py-2 text-base rounded-lg h-max transition-colors',
              exportType === 'roster'
                ? 'bg-[var(--accent)] text-white'
                : 'bg-surface text-[var(--accent)] border-[1px] border-[var(--accent)]',
            ]"
          >
            Export: Roster level
          </button>
        </div>
        <div class="flex gap-3 flex-wrap">
          <button
            v-for="(item, index) in exportOrderPriceOptions"
            :key="index"
            @click="setDateRange(item.value)"
            class="px-2 py-2 text-base text-white inline-block w-max rounded-lg last-of-type:mb-0 h-max bg-[var(--accent)]"
          >
            {{ item.text }}
          </button>
        </div>
        <div class="my-3 border-t pt-3">
          <h4 class="font-medium text-base mb-2">Group By</h4>
          <div class="flex flex-col gap-2">
            <label
              v-for="option in groupByOptions"
              :key="option.value"
              class="inline-flex items-center gap-2 cursor-pointer select-none"
            >
              <input
                type="checkbox"
                :value="option.value"
                v-model="selectedGroupBy"
                class="h-4 w-4 accent-[#00b087]"
              />
              <span class="text-sm">{{ option.label }}</span>
            </label>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="flex w-full justify-between">
          <div class="flex gap-2">
            <button
              @click="setDateRange('last_year')"
              class="px-3 py-2 text-base inline-block w-max rounded-lg mb-2 last-of-type:mb-0 h-max text-white bg-[var(--accent)]"
            >
              Last year
            </button>
            <button
              @click="setDateRange('all_time')"
              class="px-3 py-2 text-base inline-block w-max rounded-lg mb-2 last-of-type:mb-0 h-max text-white bg-[var(--accent)]"
            >
              All time
            </button>
          </div>
          <div class="gap-2 flex">
            <button
              class="dash-btn dash-btn--primary h-max"
              @click="exportOrderPrices"
            >
              Export
            </button>
            <button
              class="dash-btn h-max"
              @click="() => (exportOrderPriceModal = false)"
            >
              Cancel
            </button>
          </div>
        </div>
      </template>
    </DrawerDialog>
  </Transition>
</template>
<script setup lang="ts">
import DrawerDialog from '@/components/Shared/DrawerDialog.vue';
import { getDateRange } from '@/composables';
import { handleResponseException } from '@/interceptors/axiosRequestInterceptor';
import { useMainStore } from '@/store';
import { DocumentCurrencyDollarIcon } from '@heroicons/vue/24/outline';
import { ref } from 'vue';

const exportOrderPriceOptions = [
  { value: 'this_week', text: 'This week' },
  { value: 'last_week', text: 'Last week' },
  { value: 'this_month', text: 'This month' },
  { value: 'last_month', text: 'Last month' },
  { value: 'this_quarter', text: 'This quarter' },
  { value: 'last_quarter', text: 'Last quarter' },
  { value: 'this_year', text: 'This year' },
];

const groupByOptions = [
  { value: 'samples', label: 'Samples' },
  { value: 'remakes', label: 'Remakes' },
  { value: 'other', label: 'Other order categories' },
];
const store = useMainStore();
const exportOrderPriceModal = ref<boolean>(false);
const closeEvent = () => {
  exportOrderPriceModal.value = false;
};

const open = () => {
  exportOrderPriceModal.value = true;
};
defineExpose({ open });

const props = defineProps<{
  hideTrigger?: boolean;
  onExport: (
    fromDate: string,
    endDate: string,
    exportType: string,
    groupBy: string[],
  ) => void | Promise<void>;
}>();

const fromDate = ref<string | null>(null);
const endDate = ref<string | null>(null);
const exportType = ref<string>('order');
const selectedGroupBy = ref<string[]>([]);

const setDateRange = (option: string) => {
  try {
    if (option === 'all_time') {
      // Clear the dates for "All Time"
      fromDate.value = null;
      endDate.value = null;
    } else {
      const { from, to } = getDateRange(option);

      // Format dates to "YYYY-MM-DD"
      fromDate.value = from.toISOString().split('T')[0]; // Extract only the date
      endDate.value = to.toISOString().split('T')[0]; // Extract only the date
    }
  } catch (error: any) {
    handleResponseException(error);
  }
};

const exportOrderPrices = async (): Promise<void> => {
  await props.onExport(
    fromDate.value,
    endDate.value,
    exportType.value,
    [...selectedGroupBy.value],
  );
  exportOrderPriceModal.value = false;
};
</script>
<style lang="sass" scoped></style>
